import i18n from "utils/i18n";
import PropTypes from "prop-types";
import React from "react";
import TimeZone from "utils/time-zone";

/**
 * A <select> field that is populated with all available time zones.
 * The browser's time zone is automatically selected if `value` isn't given as a prop,
 * and it's automatically added to the list of time zone's if it doesn't already exist.
 *
 * @example
 *   <TimeZoneSelect
 *     id="used-by-label-for"
 *     name="name-for-form-submission"
 *     value="America/Chicago" />
 */
class TimeZoneSelect extends React.Component {
  /**
   * Creates the component with the initial state based on props.
   *
   * @param {object} props Initial props
   */
  constructor(props) {
    super(props);
    this.state = { value: this.defaultValue() };
    this.onChange = this.onChange.bind(this);
  }

  /**
   * Returns the default value based on the component's props.
   *
   * @returns {string} The IANA time zone name
   */
  defaultValue() {
    if (this.props.value) {
      return this.props.value;
    }

    if (this.props.defaultValue) {
      return this.props.defaultValue;
    }

    return TimeZone.browser();
  }

  /**
   * Returns whether the time zone exists in the list of available time zones.
   *
   * @returns {boolean} Whether the time zone exists
   */
  hasValue() {
    const availableValues = i18n.timeZones.us.concat(i18n.timeZones.other).map(timeZone => {
      return timeZone[0];
    });

    return availableValues.includes(this.value());
  }

  /**
   * Handles the select box change event to update the state with the selected time zone.
   *
   * @param {object} event The select onChange event
   */
  onChange(event) {
    const { value } = event.target;

    this.setState({ value }, () => {
      // Fire the onChange event after the state updates
      if (this.props.onChange != null) {
        this.props.onChange.call(null, value);
      }
    });
  }

  /**
   * Returns the selected time zone.
   *
   * @returns {string} The IANA time zone name
   */
  value() {
    return this.props.value || this.state.value;
  }

  /**
   * Renders a divider if the browser's time zone is missing from the list.
   *
   * @returns {object} The React element
   */
  render() {
    const usTimeZones = this.renderOptions("usZones", i18n.timeZones.us);
    const otherTimeZones = this.renderOptions("otherZones", i18n.timeZones.other);

    /* eslint-disable jsx-a11y/no-onchange, react/jsx-no-literals */
    return (
      <select
        className={this.props.className || "form-control"}
        disabled={this.props.disabled}
        id={this.props.id}
        name={this.props.name}
        onChange={this.onChange}
        readOnly={this.props.readOnly}
        value={this.value()}
      >
        {this.renderDefaultTimeZoneOption()}
        {this.renderDefaultTimeZoneDivider()}
        {usTimeZones}
        <option disabled="disabled">-------------</option>
        {otherTimeZones}
      </select>
    );
    /* eslint-enable jsx-a11y/no-onchange, react/jsx-no-literals */
  }

  /**
   * Renders a divider if the browser's time zone is missing from the list.
   *
   * @returns {object} The React element
   */
  renderDefaultTimeZoneDivider() {
    if (!this.hasValue()) {
      return <option disabled="disabled">-------------</option>; // eslint-disable-line react/jsx-no-literals
    }
  }

  /**
   * Renders the browser's time zone as a new select option if it's missing from the list.
   *
   * @returns {object} The React element
   */
  renderDefaultTimeZoneOption() {
    if (!this.hasValue()) {
      return (
        <option key="browser-time-zone" value={this.value()}>
          {this.value()}
        </option>
      );
    }
  }

  /**
   * Renders the select options for a group of time zones.
   *
   * @param {string} keyName The key prefix for generating unique keys for each option
   * @param {Array} zones The collection of time zones to render
   * @returns {object} The React element
   */
  renderOptions(keyName, zones) {
    return zones.map((zone, index) => {
      const key = keyName + "-" + index;

      return (
        <option key={key} value={zone[0]}>
          {zone[1]}
        </option>
      );
    });
  }
}

TimeZoneSelect.propTypes = {
  className: PropTypes.string,
  defaultValue: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
  value: (props, propName) => {
    if (!props[propName] || props.onChange || props.disabled || props.readOnly) {
      return PropTypes.checkPropTypes({ value: PropTypes.string }, props, "value", "TimeZoneSelect");
    }

    return new Error(
      "You provided a `value` prop to a form field without an " +
        "`onChange` handler. This will render a read-only field. If " +
        "the field should be mutable use `defaultValue`. Otherwise, " +
        "set either `onChange` or `readOnly`."
    );
  }
};

export default TimeZoneSelect;
